import { createComponent, ElementWithProps } from '@carvana/merch-components';
import cx from 'classnames';

/**
 * VDP Layout System
 *
 * A responsive grid-based layout system for the Vehicle Details Page that provides:
 * - Consistent spacing and alignment across breakpoints
 * - Optimal content organization for different screen sizes
 * - Sticky sidebar functionality for the vehicle info card
 *
 * Breakpoint Layout Behavior:
 * - Mobile: Single column, vertically stacked sections
 * - Tablet: Two columns, hero spans full width
 * - Desktop: Two columns, hero confined to main content area
 *
 * Grid Structure:
 * - Main content: Flexible width column with vehicle details
 * - Sidebar: Fixed width column (varies by breakpoint) containing sticky info card
 */

export const sectionXPadding = 'px-2xl sm:pr-0 md:px-0';
export const sectionTopPadding = 'u:pt-4xl sm:u:pt-5xl md:u:pt-7xl';
export const sectionBottomPadding = 'u:pb-4xl sm:u:pb-5xl md:u:pb-7xl';
export const sectionYPadding = cx(sectionTopPadding, sectionBottomPadding);
export const sectionXMargin = 'mx-2xl sm:mx-0';
export const sectionYSpacing = 'flex flex-col gap-4xl md:gap-7xl';

export const lastSectionBottomPadding =
  'last:pb-4xl last:sm:pb-5xl last:md:pb-7xl';

// Grid column definitions
const gridCols = `
  grid-cols-1
  sm:grid-cols-2
  md:grid-cols-[minmax(0,2fr)_1fr]
`;

export const GridLayout = createComponent('div', {
  className: cx(
    'relative grid justify-center gap-x-2xl bg-background-default lg:gap-x-4xl',
    gridCols
  )
});

export const MainContentWrapper = createComponent('div', {
  className: 'row-start-3 md:row-start-3'
});

export const VehicleInfoSidebar = createComponent<
  ElementWithProps<{ isCondensedLayout: boolean }>
>('div', ({ isCondensedLayout }) => ({
  className: cx(
    'z-10',
    // Grid positioning - moves to second column and spans two rows on larger screens
    'sm:col-start-2 sm:row-span-3',
    isCondensedLayout ? 'sm:row-start-1' : 'sm:row-start-3 md:row-start-1',
    // Responsive padding:
    // - Mobile: Edge-to-edge layout
    // - Tablet: Added spacing for content separation (left column handles left spacing)
    // - Desktop: Grid handles spacing
    'sm:pr-2xl md:pr-0 md:pt-0',
    'sm:pb-5xl md:pb-7xl'
  )
}));

export const StickyWrapper = createComponent('div', {
  className:
    'sticky top-[var(--sticky-info-card-top-sm,32px)] md:top-[var(--sticky-info-card-top-md,32px)]'
});

export const HigherSectionsWrapper = createComponent<
  ElementWithProps<{ hideBottomPadding?: boolean }>
>('div', ({ hideBottomPadding }) => ({
  className: cx(
    'sm:pl-2xl md:pl-0 md:u:pb-7xl',
    sectionYSpacing,
    hideBottomPadding
      ? 'border-b border-border-weak u:pb-0 sm:border-none sm:u:pb-4xl'
      : 'u:pb-4xl'
  )
}));

export const DealershipHigherSectionsWrapper = createComponent<
  ElementWithProps<{ hideBottomPadding: boolean }>
>(HigherSectionsWrapper, () => ({ className: 'u:pt-2xl sm:u:pt-0' }));

export const LowerSectionsWrapper = createComponent('div', {
  className: cx(
    'flex flex-col'
    // Responsive padding:
    // - Mobile: Full content margins
    // - Tablet+: Grid system handles spacing
  )
});

export const RecommendedVehiclesSectionWrapper = createComponent('div', {
  className: cx(
    'border-t border-border-weak py-4xl sm:border-none sm:pt-0',
    sectionBottomPadding,
    sectionXPadding
  )
});

export const PaddedWrapper = createComponent('div', {
  className:
    'flex w-full flex-col md:mx-auto md:max-w-[468px] lg:max-w-[588px] xl:max-w-[624px]'
});
